import {graphql, usePaginationFragment} from 'react-relay'

import type {IssueTimelineIssueFragment$data} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {NewTimelinePaginationBackQuery} from './__generated__/NewTimelinePaginationBackQuery.graphql'
import type {NewTimelinePaginationFrontQuery} from './__generated__/NewTimelinePaginationFrontQuery.graphql'
import type {useTimelineItemsBackFragment$key} from './__generated__/useTimelineItemsBackFragment.graphql'
import type {useTimelineItemsFrontFragment$key} from './__generated__/useTimelineItemsFrontFragment.graphql'
import {createBackFetcherConfig, useTimelineItemsCore} from './use-timeline-items-core'

type UseTimelineItemsProps = {
  timelineData: IssueTimelineIssueFragment$data
  highlightedEvent: string | undefined
  page?: number
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const frontTimelineItemsFragment = graphql`
  fragment useTimelineItemsFrontFragment on Issue
  @argumentDefinitions(count: {type: "Int", defaultValue: 15}, cursor: {type: "String"}, skip: {type: "Int"})
  @refetchable(queryName: "NewTimelinePaginationFrontQuery") {
    frontTimelineItems: timelineItems(first: $count, visibleEventsOnly: true, after: $cursor, skip: $skip)
      @defer(label: "Issue__frontTimelineItems")
      @connection(key: "Issue__frontTimelineItems") {
      pageInfo {
        hasNextPage
      }
      totalCount
      edges {
        node {
          __id
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...IssueTimelineItem
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const backTimelineItemsFragment = graphql`
  fragment useTimelineItemsBackFragment on Issue
  @argumentDefinitions(count: {type: "Int", defaultValue: 0}, cursor: {type: "String"})
  @refetchable(queryName: "NewTimelinePaginationBackQuery") {
    backTimelineItems: timelineItems(last: $count, visibleEventsOnly: true, before: $cursor)
      @defer(label: "Issue__backTimelineItems")
      @connection(key: "Issue__backTimelineItems") {
      pageInfo {
        hasPreviousPage
      }
      totalCount
      edges {
        node {
          __id
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...IssueTimelineItem
        }
      }
    }
  }
`

// Custom query for loading more back timeline items to avoid being limited by usePaginationFragment
// we can not call loadMore or loadPrevious if the main query is still in flight
// however, since we return data from the cache sometimes the main query is always in flight leading to an unusable loadPrevious function
// using its own query we can avoid that problem
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const useTimelineItemsBackQuery = graphql`
  query useTimelineItemsBackQuery($id: ID!, $count: Int!, $cursor: String) {
    node(id: $id) {
      ... on Issue {
        backTimelineItems: timelineItems(last: $count, visibleEventsOnly: true, before: $cursor) {
          pageInfo {
            startCursor
            hasPreviousPage
          }
          totalCount
          edges {
            node {
              __id
              # eslint-disable-next-line relay/must-colocate-fragment-spreads
              ...IssueTimelineItem
            }
          }
        }
      }
    }
  }
`

const classicBackFetcherConfig = createBackFetcherConfig(useTimelineItemsBackQuery, 'backTimelineItems')

/**
 * Classic (unfiltered) timeline hook.
 *
 * Reads from the classic front/back fragments and delegates pagination,
 * highlight-loading, and timeline stitching to the shared core hook.
 */
export const useTimelineItems = ({timelineData, highlightedEvent, page}: UseTimelineItemsProps) => {
  const {
    data: {frontTimelineItems: frontTimelineData},
    loadNext: loadMoreFrontItems,
    hasNext: frontHasNextPage,
  } = usePaginationFragment<NewTimelinePaginationFrontQuery, useTimelineItemsFrontFragment$key>(
    frontTimelineItemsFragment,
    timelineData,
  )

  const {
    data: {backTimelineItems: backTimelineData},
  } = usePaginationFragment<NewTimelinePaginationBackQuery, useTimelineItemsBackFragment$key>(
    backTimelineItemsFragment,
    timelineData,
  )

  return useTimelineItemsCore({
    issueId: timelineData.id,
    frontData: frontTimelineData,
    backData: backTimelineData,
    loadMoreFrontItems,
    backFetcherConfig: classicBackFetcherConfig,
    frontHasNextPage,
    highlightedEvent,
    page,
  })
}
